import type { TenantStatus as TenantStatusType } from './enums';

/** tenant status ライフサイクルの単一定義。
 *  各状態に「この状態から抜け出す責任者（owner）」を明記する —
 *  past_due が無期限に放置された事故の構造的再発防止。
 *
 *  `terminal` の意味は 2 つの現行ガードで一致している（下記テストで固定）:
 *    - stripe-client/src/sync.ts `TERMINAL_TENANT_STATES`
 *        … webhook/reconcile による status 上書きを禁止する集合
 *    - apps/admin/src/lib/tenant-status.ts `isTenantTerminal`
 *        … UI アクセスを遮断する集合
 *  両者は歴史的に同一集合であり、この表がその単一の出所となる。
 *  もし将来どちらかだけを変える必要が出たら、この表を分割せずに
 *  差分を別集合として明示し、挙動差をコメントで残すこと。 */
export const TENANT_LIFECYCLE = {
  provisioning: { terminal: true, owner: 'provisioning-saga' },
  active: { terminal: false, owner: 'stripe-webhook' },
  past_due: { terminal: false, owner: 'dunning-sweep (14d) / stripe-webhook' },
  suspended: { terminal: false, owner: 'stripe-webhook (payment) / billing-reconcile' },
  cancelling: { terminal: false, owner: 'stripe-webhook (period end)' },
  cancelled: { terminal: true, owner: 'tenant-purge-sweep (grace period)' },
  purge_scheduled: { terminal: true, owner: 'tenant-purge-sweep' },
  purged: { terminal: true, owner: '(end state)' },
  failed: { terminal: true, owner: 'ops manual review' },
} as const satisfies Record<TenantStatusType, { terminal: boolean; owner: string }>;

/** 終端状態の集合。sync.ts の TERMINAL_TENANT_STATES / tenant-status.ts の
 *  isTenantTerminal が導出元とする単一の真実。 */
export const TERMINAL_TENANT_STATUSES: ReadonlySet<TenantStatusType> = new Set(
  (Object.entries(TENANT_LIFECYCLE) as [TenantStatusType, { terminal: boolean }][])
    .filter(([, def]) => def.terminal)
    .map(([status]) => status),
);

/** status が終端状態か。string を受けるのは Svelte テンプレート/DB 生値からの
 *  呼び出しを薄く保つため（未知の値は非終端として扱う）。 */
export function isTenantTerminalStatus(status: string): boolean {
  return TERMINAL_TENANT_STATUSES.has(status as TenantStatusType);
}
